/**
 * Service worker registration (owner B1). Production builds only; a no-op under `vite dev`.
 *
 * Call once at boot (I1, src/app). The worker (public/sw.js) makes repeat visits instant and lets
 * the kiosk start offline. Because the first visit loads before the worker controls the page, the
 * page then tells it which bundles, fonts and glyph ranges it already loaded ("ld:warm"), so an
 * offline reload finds them. The map packs (/a/*) are written to Cache Storage by the preloader.
 */
import { on } from "@/store/bus";

/** Same-origin resources the worker should hold for an offline start. */
export function warmUrls(entries: ReadonlyArray<{ name: string }>, origin: string): string[] {
  const out = new Set<string>();
  for (const e of entries) {
    let u: URL;
    try {
      u = new URL(e.name);
    } catch {
      continue;
    }
    if (u.origin !== origin) continue;
    if (u.pathname.startsWith("/assets/") || u.pathname.startsWith("/glyphs/")) out.add(u.pathname);
  }
  return [...out];
}

function postWarm(reg: ServiceWorkerRegistration): void {
  const sw = reg.active ?? navigator.serviceWorker.controller;
  if (!sw) return;
  const urls = warmUrls(performance.getEntriesByType("resource"), location.origin);
  if (urls.length) sw.postMessage({ type: "ld:warm", urls });
}

let registered = false;

export function registerSW(): void {
  if (!import.meta.env.PROD || registered) return;
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !window.isSecureContext) return;
  registered = true;

  const go = () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .then(() => navigator.serviceWorker.ready)
      .then((reg) => {
        postWarm(reg);
        // Resources that arrive before the worker takes control (lazy chunks, glyph ranges) are
        // reported again once it does, and after the map and the DEM have settled.
        navigator.serviceWorker.addEventListener("controllerchange", () => postWarm(reg), { once: true });
        const offMap = on("map:ready", () => {
          offMap();
          setTimeout(() => postWarm(reg), 4000);
        });
        const offDem = on("dem:ready", () => {
          offDem();
          setTimeout(() => postWarm(reg), 2000);
        });
      })
      .catch((e: unknown) => console.warn("[sw] registration failed", e));
  };
  if (document.readyState === "complete") go();
  else window.addEventListener("load", go, { once: true });
}
